import type {
  CustomerNotification,
  CustomerNotificationMetadata,
} from '@kizen/api/customer-notifications';
import type { EmployeePageConfigs } from '@kizen/api/employee';
import type { TypographyProps } from '@kizen/kds/Typography';
import type { UIEvent } from 'react';
import type {
  Action,
  BulkAction,
  FilterState,
  Option,
  TableState,
} from './context';

import css from '@emotion/css';
import styled from '@emotion/styled';
import { updateEmployeePageConfig } from '@kizen/api/employee';
import { Button } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { format } from 'date-fns';
import { breakpoints, useBreakpoint } from '__app/spacing';
import { KizenTypography } from '__app/typography';
import ButtonWithDropdown from '__components/Button/WithDropdown';
import {
  DateRangePill,
  MultiSelectPill,
  SingleSelectPill,
} from 'components/Inputs/PillDropdown/Inputs';
import Icon from '__components/Kizen/Icon';
import IconButton from '__components/Kizen/IconButton';
import Loader from '__components/Kizen/Loader';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { SizedCell, SortableHeadCell } from '__components/Kizen/Table';
import { ContentWidth, PageSizing } from '__components/Layout/PageContentWidth';
import PageToolbar, {
  PageToolbarSection,
  PageSearchInput,
  PageToolbarTitle,
} from '__components/Layout/PageToolbar';
import ActionCell from '__components/Tables/ActionCell';
import BasicTable, { BasicTableWrapper } from '__components/Tables/Basic';
import TableScrollContainer from '__components/Tables/ScrollContainer';
import useEndsOfScroll from '__hooks/useEndsOfScroll';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import {
  BulkNotificationActionModal,
  useBulkNotificationActionOptions,
} from 'pages/Common/bulkactions/BulkNotificationActionModal';
import {
  BasicCheckboxCell,
  BasicCheckboxMenuCell,
} from '__pages/Common/components/BigTableCells';
import { customerNotificationsMetadataQuery } from 'query/customer-notifications';
import AxiosService from 'services/AxiosService';
import { ButtonRadio } from 'ts-components/ButtonToggle';
import { collapseNumber } from 'utility/numbers';
import { CommentsModal } from './CommentsModal';
import { NotificationsPageProvider, useNotificationsPage } from './context';
import { NotificationStatusCell } from './NotificationStatus';

const TABLE_LAYERS = {
  CHECKBOX_CELL: {
    BODY: 1,
    HEADER: 4,
  },
  GRADIENT: 3,
  HEADER: 2,
  SCROLLBAR: 5,
};

const CELL_LEFT_PADDING = { paddingLeft: '15px' };

const ICON_COLOR = {
  default: 'var(--color-button-tertiary-default)',
  hover: 'var(--color-button-tertiary-hover)',
};

const StyledPageSearchInput = styled(PageSearchInput)`
  ${({ isMobile }) =>
    isMobile &&
    css`
      grid-row: 1;
      grid-column: 3;
      justify-self: flex-end;
    `}
`;

const ClearFilterButton = styled(IconButton)`
  svg {
    width: 12px;
    height: initial;
  }
`;

const PerformActionButton = styled(ButtonWithDropdown)`
  button {
    background-color: var(--color-button-secondary-default);
    border-color: var(--color-button-secondary-default);
  }
`;

const PageGrid = styled(ContentWidth)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 75px 70px minmax(0, max-content);
  height: calc(100vh - 51px - 50px);

  & > div:last-of-type {
    min-height: 0;
  }
`;

const StyledPageToolbar = styled(PageToolbar, {
  shouldForwardProp: (name) => name !== 'reversed',
})<{ reversed?: boolean }>`
  ${({ reversed = false }) =>
    reversed &&
    css`
      flex-direction: row-reverse;

      > div:first-of-type {
        justify-content: flex-end;
      }
    `}
`;

const TopControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 15px;
  margin-bottom: 15px;
  border-bottom: 1px solid var(--color-border-medium-light);
`;

const TopControlsLeftContainer = styled.div`
  display: inline-flex;
  gap: 15px;
  align-items: center;
`;

const TopControlsRightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  width: calc(var(--notifications-perform-action-button-width) * 1px);
`;

const Table = styled(BasicTable)`
  min-width: 620px;

  && thead {
    z-index: ${TABLE_LAYERS.HEADER};
  }

  thead > tr > th:first-of-type {
    z-index: ${TABLE_LAYERS.CHECKBOX_CELL.HEADER};
  }
`;

const CheckboxCell = styled(BasicCheckboxCell)`
  z-index: ${TABLE_LAYERS.CHECKBOX_CELL.BODY};

  ${({ isScrolledLeft }) =>
    !isScrolledLeft &&
    css`
      background-color: var(--color-background-white);
    `}
`;

const GradientLeft = styled(Gradient)`
  z-index: ${TABLE_LAYERS.GRADIENT};

  ${({ hideBulkActions }) =>
    !hideBulkActions &&
    css`
      left: 46px;
    `}
`;

const getActionOptions = (data: {
  notification: CustomerNotification;
  metadata: CustomerNotificationMetadata;
  t: (x: string) => string;
}) => {
  const { notification, metadata, t } = data;
  const { action_labels, primary_actions } = metadata;
  const options: Option<Action>[] = [];
  let primary: Action | null = null;

  if (notification.read) {
    options.push({ value: { type: 'mark_unread' }, label: t('Mark Unread') });
  } else {
    options.push({ value: { type: 'mark_read' }, label: t('Mark Read') });
  }

  if (notification.archived) {
    options.push({ value: { type: 'unarchive' }, label: t('Unarchive') });
  } else {
    options.push({ value: { type: 'archive' }, label: t('Archive') });
  }

  if (notification.actions) {
    for (const action of notification.actions) {
      if (primary_actions[notification.notification_type] === action.type) {
        primary = action;
        continue;
      }

      if ('label' in action && action.label) {
        options.push({ value: action, label: action.label });
      } else {
        options.push({ value: action, label: action_labels[action.type] });
      }
    }
  }

  return { options, primary };
};

const updatePageConfig = (options: {
  filters: FilterState;
  search: string;
}) => {
  const { filters, search } = options;
  const config: EmployeePageConfigs['notification_center'] = {};

  if (filters.scope) {
    config.scope = filters.scope;
  }

  if (filters.read_status) {
    config.read = filters.read_status === 'read';
  }

  if (filters.created) {
    config.created = filters.created;
  }

  if (filters.archive_status) {
    config.archived = filters.archive_status;
  }

  if (filters.notification_type.length) {
    config.notification_type = filters.notification_type.map(
      (opt) => opt.value
    );
  }

  if (filters.status.length) {
    config.status = filters.status.map((opt) => opt.value);
  }

  if (search) {
    config.search = search;
  }

  if (filters.comments === 'has_comments') {
    config.has_comments = true;
  } else if (filters.comments === 'is_me') {
    config.mentions = 'is_me';
  }

  return updateEmployeePageConfig(AxiosService, 'notification_center', {
    config,
  });
};

const Page = () => {
  const { t } = useTranslation();
  const hideFilters = useBreakpoint(1140);
  const hideBulkActions = hideFilters;
  const isMobile = useBreakpoint(breakpoints.sm);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const performActionRef = useRef<HTMLDivElement>();
  const horizontalScrollRef = useRef<HTMLDivElement>();
  const [tableWrapperRef, tableRect] = useMeasure();
  const scrolls = useEndsOfScroll(horizontalScrollRef, [
    tableRect.height,
    tableRect.width,
  ]);
  const isScrolledRight = scrolls[1];
  const isScrolledLeft = scrolls[3];

  useSetTitleOnLoad(t('Notification Center'));

  const {
    bulkAction,
    checked,
    group,
    filters,
    metadata,
    notifications,
    params,
    query,
    scrollRef,
    search,
    clearFilters,
    hideBulkActionModal,
    onAllCheckedChange,
    onCheckNotification,
    onFilterChange,
    commenting,
    onOpenCommentModal,
    onHideCommentModal,
    onRefresh,
    onSaveComment,
    onSearch,
    onSelectBulkAction,
    performAction,
    performBulkAction,
    showBulkActionModal,
  } = useNotificationsPage();

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  const handleScroll = useCallback(
    (ev: UIEvent<HTMLDivElement>) => {
      if (hasNextPage && !isFetchingNextPage) {
        const target = ev.currentTarget;
        const threshold = target.scrollHeight * 0.7;

        if (target.scrollTop + target.clientHeight >= threshold) {
          fetchNextPage();
        }
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const { table } = metadata;

  const bulkActions = useBulkNotificationActionOptions();

  const groupOptions: Option<TableState['group']>[] = [
    { value: 'search', label: t('All in Search') },
    { value: 'all', label: t('All Notifications') },
    { value: 'none', label: t('None') },
  ];

  const head = (
    <tr>
      {!hideBulkActions && (
        <BasicCheckboxMenuCell
          head
          checked={checked.size > 0}
          indeterminate={checked.size > 0 && group !== 'all'}
          zIndex={TABLE_LAYERS.CHECKBOX_CELL.HEADER}
          options={groupOptions}
          onChange={(option: Option<TableState['group']>) => {
            if (option.value === 'all' || option.value === 'search') {
              onAllCheckedChange({ group: option.value, notifications });
            } else {
              onAllCheckedChange({ group: option.value });
            }
          }}
        />
      )}
      {table.columns.map((column, i) => {
        const style = hideBulkActions && i === 0 ? CELL_LEFT_PADDING : null;

        if (column === 'actions') {
          return (
            <SortableHeadCell
              head
              key={column}
              style={style}
              hasSorting={false}
              column={{ id: column }}
              label={table.labels.actions}
              align="right"
              padding="15px"
            />
          );
        }

        return (
          <SortableHeadCell
            head
            key={column}
            style={style}
            hasSorting={false}
            column={{ id: column }}
            label={table.labels[column]}
            width={table.widths[column]}
            padding="30px"
          />
        );
      })}
    </tr>
  );

  return (
    <PageSizing
      style={{
        '--notifications-perform-action-button-width':
          performActionRef.current?.clientWidth,
      }}
    >
      {commenting && (
        <CommentsModal
          notification={commenting}
          onHide={onHideCommentModal}
          onSaveComment={onSaveComment}
        />
      )}
      {bulkAction.open && (
        <BulkNotificationActionModal
          action={bulkAction.action}
          group={group}
          ids={checked}
          params={params}
          search={search}
          onHide={hideBulkActionModal}
          onSelectAction={onSelectBulkAction}
          onSelectGroup={(value) => {
            if (value === 'all' || value === 'search') {
              onAllCheckedChange({ group: value, notifications });
            }
          }}
          onComplete={({ count, key }) => {
            if (bulkAction.action) {
              performBulkAction({ action: bulkAction.action, count, key });
            }
          }}
          onGoBack={() => {
            onSelectBulkAction(null);
          }}
        />
      )}
      <PageGrid>
        <StyledPageToolbar reversed={hideBulkActions}>
          <PageToolbarSection>
            <StyledPageSearchInput
              placeholder={t('Find Notifications')}
              isMobile={isMobile}
              value={search}
              onChange={(value: string) => {
                onSearch(value);
                updatePageConfig({ filters, search: value });
              }}
            />
          </PageToolbarSection>
          <PageToolbarTitle>{t('Notifications')}</PageToolbarTitle>

          {!hideBulkActions && (
            <PageToolbarSection>
              <PerformActionButton
                color="blue"
                ref={performActionRef}
                options={bulkActions}
                onClick={showBulkActionModal}
                onChange={(opt: Option<BulkAction>) => {
                  onSelectBulkAction(opt.value);
                }}
              >
                {t('Perform Action')}
              </PerformActionButton>
            </PageToolbarSection>
          )}
        </StyledPageToolbar>
        <TopControlsContainer>
          <TopControlsLeftContainer>
            {!hideFilters && (
              <>
                <KizenTypography
                  color="var(--color-font-placeholder)"
                  weight="bold"
                  textTransform="uppercase"
                  size="12px"
                >
                  {t('Quick Filters')}
                </KizenTypography>
                {metadata.filters.map((filter) => {
                  if (filter.input_type === 'singleselect') {
                    return (
                      <SingleSelectPill
                        key={filter.name}
                        title={filter.label}
                        placeholder={filter.placeholder}
                        value={filters[filter.name]}
                        options={filter.options}
                        onChange={(option) => {
                          const value = option?.value ?? null;

                          onFilterChange(filter, value);
                          updatePageConfig({
                            filters: { ...filters, [filter.name]: value },
                            search,
                          });
                        }}
                      />
                    );
                  }

                  if (filter.input_type === 'date_range_inclusive') {
                    return (
                      <DateRangePill
                        key={filter.name}
                        title={filter.label}
                        value={filters[filter.name]}
                        onChange={(value) => {
                          onFilterChange(filter, value);
                          updatePageConfig({
                            filters: { ...filters, [filter.name]: value },
                            search,
                          });
                        }}
                      />
                    );
                  }

                  if (filter.input_type === 'multiselect') {
                    return (
                      <MultiSelectPill
                        key={filter.name}
                        title={filter.label}
                        placeholder={filter.placeholder}
                        value={filters[filter.name]}
                        options={filter.options}
                        onChange={(opts) => {
                          onFilterChange(filter, opts);
                          updatePageConfig({
                            filters: { ...filters, [filter.name]: opts },
                            search,
                          });
                        }}
                      />
                    );
                  }

                  return null;
                })}
                <ClearFilterButton
                  sizing="dense"
                  title={t('Clear All Quick Filters')}
                  color="var(--color-button-secondary-default)"
                  onClick={clearFilters}
                >
                  <Icon icon="close-regular" />
                </ClearFilterButton>
              </>
            )}
          </TopControlsLeftContainer>
          <TopControlsRightContainer>
            <ButtonRadio
              highlightBackground
              variant="outline"
              height="26px"
              optionWidth="45px"
              color="var(--color-button-secondary-default)"
              fontColor="var(--color-button-secondary-default)"
              backgroundColor="var(--color-background-white)"
              hoverColor="var(--color-button-secondary-hover)"
              selectedColor="var(--color-background-white)"
              value={filters.archive_status}
              onChange={(value) => {
                onFilterChange({ name: 'archive_status' }, value);
                updatePageConfig({
                  filters: { ...filters, archive_status: value },
                  search,
                });
              }}
              options={
                [
                  { value: null, iconName: 'inbox', label: t('Inbox') },
                  {
                    value: 'archived',
                    iconName: 'box-archive',
                    label: t('Archive'),
                  },
                  {
                    value: 'all',
                    iconName: 'check-double',
                    label: t('Inbox and Archive'),
                  },
                ] as const
              }
            />
            <Button
              variant="text"
              rightIcon="action-refresh"
              color={query.isRefetching ? 'inherit' : 'secondary'}
              disabled={query.isRefetching}
              onClick={onRefresh}
            />
          </TopControlsRightContainer>
        </TopControlsContainer>
        {query.isLoading && <Loader loading />}
        {!query.isLoading && (
          <BasicTableWrapper ref={tableWrapperRef} className="shadow-tile">
            <TableScrollContainer
              bottom
              zIndex={TABLE_LAYERS.SCROLLBAR}
              ref={horizontalScrollRef}
              key={tableRect.height.toFixed()}
              verticalScrollRef={scrollRef}
              onScroll={handleScroll}
            >
              <GradientLeft
                position="left"
                fadeIn={!isScrolledLeft}
                zIndex={TABLE_LAYERS.GRADIENT}
                hideBulkActions={hideBulkActions}
              />
              <Table stickyHeader head={head}>
                {notifications.map((notification) => {
                  const weight: TypographyProps['weight'] = notification.read
                    ? undefined
                    : 'bold';

                  const isRowHovered = hoveredId === notification.id;

                  return (
                    <tr
                      key={notification.id}
                      data-row-id={notification.id}
                      onMouseLeave={() => setHoveredId(null)}
                      onMouseOver={() => setHoveredId(notification.id)}
                    >
                      {!hideBulkActions && (
                        <CheckboxCell
                          isScrolledLeft={isScrolledLeft}
                          checked={checked.has(notification.id)}
                          onChange={() => onCheckNotification(notification)}
                        />
                      )}
                      {table.columns.map((column, i) => {
                        const style =
                          hideBulkActions && i === 0
                            ? CELL_LEFT_PADDING
                            : undefined;

                        if (column === 'status') {
                          return (
                            <NotificationStatusCell
                              key={column}
                              style={style}
                              notification={notification}
                              weight={weight}
                            />
                          );
                        }

                        if (column === 'actions') {
                          const { options, primary } = getActionOptions({
                            notification,
                            metadata,
                            t,
                          });

                          return (
                            <ActionCell
                              key={column}
                              style={style}
                              title={t('Select Action')}
                              menuType="absolute"
                              hasPrimaryAction={Boolean(primary)}
                              width={table.widths.actions}
                              padding="15px"
                              options={options}
                              onSelectAction={(opt: Option<Action>) => {
                                setHoveredId(null);
                                performAction(opt.value, notification);
                              }}
                            >
                              {primary && (
                                <IconButton
                                  sizing="dense"
                                  title={metadata.action_labels[primary.type]}
                                  color={ICON_COLOR}
                                  onClick={() => {
                                    performAction(primary, notification);
                                  }}
                                >
                                  <Icon
                                    width="16px"
                                    icon={metadata.action_icons[primary.type]}
                                  />
                                </IconButton>
                              )}
                            </ActionCell>
                          );
                        }

                        const isLeftOfStatus =
                          table.columns[i + 1] === 'status';

                        return (
                          <SizedCell
                            key={column}
                            style={style}
                            padding={isLeftOfStatus ? '25px' : '15px'}
                            width={table.widths[column]}
                          >
                            {column === 'created' ? (
                              <Typography truncate weight={weight}>
                                {format(
                                  notification.created,
                                  'MM/dd/yyyy h:mm aa'
                                )}
                              </Typography>
                            ) : column === 'notification_type' ? (
                              <Typography truncate weight={weight}>
                                {table.labels[notification.notification_type]}
                              </Typography>
                            ) : column === 'folder' ? (
                              <Typography truncate weight={weight}>
                                {notification.archived
                                  ? t('Archive')
                                  : t('Inbox')}
                              </Typography>
                            ) : column === 'count' && notification.count > 1 ? (
                              <Typography truncate weight={weight}>
                                {collapseNumber(notification[column])}
                              </Typography>
                            ) : column === 'title' ? (
                              <div className="grid grid-cols-[minmax(0,1fr)_40px] gap-[15px] items-center">
                                <Typography
                                  truncate
                                  modalCompatibility
                                  weight={weight}
                                >
                                  {notification[column]}
                                </Typography>
                                {notification.comment_count > 0 ? (
                                  <IconButton
                                    sizing="grid"
                                    color={ICON_COLOR}
                                    title={t('Add Comment')}
                                    onClick={() =>
                                      onOpenCommentModal(notification)
                                    }
                                  >
                                    <Icon
                                      withBadge
                                      isStaticBadge
                                      icon="comment"
                                      width="16px"
                                      badgeHeight={14}
                                      badgeText={
                                        notification.comment_count > 99
                                          ? '99+'
                                          : notification.comment_count.toString()
                                      }
                                    />
                                  </IconButton>
                                ) : isRowHovered &&
                                  notification.scope === 'BUSINESS' ? (
                                  <IconButton
                                    sizing="grid"
                                    color={ICON_COLOR}
                                    title={t('Add Comment')}
                                    onClick={() =>
                                      onOpenCommentModal(notification)
                                    }
                                  >
                                    <Icon icon="message-plus" width="16px" />
                                  </IconButton>
                                ) : null}
                              </div>
                            ) : null}
                          </SizedCell>
                        );
                      })}
                    </tr>
                  );
                })}
              </Table>
              <Gradient
                position="right"
                fadeIn={!isScrolledRight}
                zIndex={TABLE_LAYERS.GRADIENT}
              />
            </TableScrollContainer>
          </BasicTableWrapper>
        )}
      </PageGrid>
    </PageSizing>
  );
};

export const NotificationsPage = () => {
  const query = useQuery(
    customerNotificationsMetadataQuery({
      staleTime: Infinity,
    })
  );

  return (
    <NotificationsPageProvider>
      {!query.isLoading && <Page />}
    </NotificationsPageProvider>
  );
};
